import { theme } from '@Guidelines/Theme/theme';
import { css } from '@emotion/react';
import useSubjectTypes from '@UI/Hooks/useSubjectTypes';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import type * as React from 'react';
import { useParams } from 'react-router-dom';
import { Message, Spinner } from '@verclam/ui-kit';

const spinnerContainerStyle = css`
	display: flex;
	justify-content: center;
	padding: ${theme.layout.gutter};
`;

const messageContainerStyle = css`
	padding: ${theme.layout.gutter};
`;

type Props = {
	readonly children: React.ReactNode;
};

/**
 * Route guard: resolves `:subjectType` among the types the account serves. An unknown key and an
 * inaccessible type get the same refusal, so the message never says whether the type exists.
 */
const RequireSubjectType: React.FC<Props> = ({ children }) => {
	const { subjectType } = useParams();
	const { user } = useStrictAuthenticatedUser();
	const { types, error } = useSubjectTypes();
	const hasAccount = (user.account?.id ?? null) !== null;

	if (hasAccount && types === null && error === null) {
		return (
			<div css={spinnerContainerStyle}>
				<Spinner aria-label="Vérification des droits d'accès" />
			</div>
		);
	}

	const isServed = subjectType !== undefined && types !== null && types.findByKey(subjectType) !== null;

	if (!isServed) {
		return (
			<div css={messageContainerStyle}>
				<Message severity="error" text="Vous n'avez pas accès à ce type de dossier." />
			</div>
		);
	}

	return children;
};

export default RequireSubjectType;
